/* ==========================================================================
   SkinTalk.mx - Facet Filters JavaScript
   Custom element <facet-filters-form> that handles:
   - AJAX filtering via Section Rendering API
   - Sort dropdown changes
   - Price range debounced inputs
   - Mobile filter drawer open/close with body scroll lock
   - Grid view toggle with localStorage persistence
   - URL manipulation without full page reload
   - Loading state management
   - Product count updates after filtering

   Table of Contents:
   1. FacetFiltersForm Custom Element
   2. View Toggle Handler
   3. Initialization
   ========================================================================== */


/* ==========================================================================
   1. FACET FILTERS FORM CUSTOM ELEMENT
   ========================================================================== */

function defineFacetFiltersForm() {
if (!customElements.get('facet-filters-form')) { class FacetFiltersForm extends SkinTalkElement {
  constructor() {
    super();

    /** @type {string} Section ID for Section Rendering API */
    this.sectionId = this.dataset.sectionId;

    /** @type {string} Collection base URL */
    this.collectionUrl = this.dataset.collectionUrl;

    /** @type {AbortController|null} Current fetch abort controller */
    this.abortController = null;

    /** @type {Function} Debounced price handler */
    this.debouncedPriceChange = SkinTalk.debounce(
      this.handlePriceChange.bind(this),
      500
    );
  }

  connectedCallback() {
    super.connectedCallback();
    this.bindEvents();
    this.restoreViewPreference();
  }

  /* ------------------------------------------------------------------
     Event Binding
     ------------------------------------------------------------------ */

  bindEvents() {
    // Checkbox change events (filter selections)
    this.on(this, 'change', (e) => {
      const target = e.target;

      if (target.matches('.facets__checkbox')) {
        this.handleFilterChange(target.closest('[data-facets-form]'));
      }

      if (target.matches('[data-sort-select]')) {
        this.handleSortChange(target.value);
      }
    });

    // Price range inputs (debounced)
    this.qsa('[data-price-min], [data-price-max]').forEach((input) => {
      this.on(input, 'input', () =>
        this.debouncedPriceChange(input.closest('[data-facets-form]'))
      );
    });

    // Mobile filter drawer open
    this.qsa('[data-open-filters]').forEach((btn) => {
      this.on(btn, 'click', () => this.openFilterDrawer());
    });

    // Mobile filter drawer close
    this.qsa('[data-close-filters]').forEach((btn) => {
      this.on(btn, 'click', () => this.closeFilterDrawer());
    });

    // Keyboard handling for drawer
    this.on(document, 'keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeFilterDrawer();
      }
    });

    // Browser Back/Forward: re-render the collection from the URL state
    // (do NOT push a new history entry, or we'd trap the user in a loop).
    this.on(window, 'popstate', () => {
      const searchParams = new URLSearchParams(window.location.search);
      this.fetchAndRender(searchParams, false);
    });

    // Active filter tag clicks (URL navigation via AJAX)
    this.on(this, 'click', (e) => {
      const filterTag = e.target.closest('.active-filter-tag');
      if (filterTag) {
        e.preventDefault();
        const url = filterTag.getAttribute('href');
        if (url) {
          this.navigateToUrl(url);
        }
      }
    });

    // View toggle buttons
    this.qsa('[data-view-columns]').forEach((btn) => {
      this.on(btn, 'click', () => {
        const columns = btn.dataset.viewColumns;
        this.setGridColumns(columns);
      });
    });

    // Handle clear filter links via AJAX
    this.on(this, 'click', (e) => {
      const clearLink = e.target.closest('.facets__clear');
      if (clearLink) {
        e.preventDefault();
        const url = clearLink.getAttribute('href');
        if (url) {
          this.navigateToUrl(url);
        }
      }
    });
  }

  /* ------------------------------------------------------------------
     Filter Change Handler
     ------------------------------------------------------------------ */

  handleFilterChange(sourceForm) {
    // Only read the form the change originated from. The sidebar and the
    // mobile drawer each render a full duplicate copy of every checkbox; if we
    // merged all forms, unchecking a filter in one would leave its still-checked
    // twin in the other, silently re-adding the filter we just removed.
    const searchParams = this.buildSearchParams(this.formsFrom(sourceForm));
    this.fetchAndRender(searchParams);
  }

  /* ------------------------------------------------------------------
     Resolve which form(s) to read params from
     ------------------------------------------------------------------ */

  formsFrom(sourceForm) {
    if (sourceForm) return [sourceForm];
    // Fallback: use the first form (all forms are in sync after each render).
    const first = this.querySelector('[data-facets-form]');
    return first ? [first] : [];
  }

  /* ------------------------------------------------------------------
     Sort Change Handler
     ------------------------------------------------------------------ */

  handleSortChange(sortValue) {
    const searchParams = this.buildSearchParams(this.formsFrom());
    searchParams.set('sort_by', sortValue);
    this.fetchAndRender(searchParams);
  }

  /* ------------------------------------------------------------------
     Price Range Handler
     ------------------------------------------------------------------ */

  handlePriceChange(sourceForm) {
    const searchParams = this.buildSearchParams(this.formsFrom(sourceForm));
    this.fetchAndRender(searchParams);
  }

  /* ------------------------------------------------------------------
     URL Navigation (for active filter removal)
     ------------------------------------------------------------------ */

  navigateToUrl(url) {
    const urlObj = new URL(url, window.location.origin);
    this.fetchAndRender(urlObj.searchParams);
  }

  /* ------------------------------------------------------------------
     Build Search Params from All Filter Forms
     ------------------------------------------------------------------ */

  buildSearchParams(forms) {
    const params = new URLSearchParams();

    forms.forEach((form) => {
      const formData = new FormData(form);

      for (const [key, value] of formData.entries()) {
        // Skip empty price values (they represent "no filter")
        if (
          (key.includes('filter.v.price.gte') || key.includes('filter.v.price.lte')) &&
          value === ''
        ) {
          continue;
        }

        // Convert price from display amount back to cents for Shopify
        if (key.includes('filter.v.price.gte') || key.includes('filter.v.price.lte')) {
          const cents = Math.round(parseFloat(value) * 100);
          if (!isNaN(cents)) {
            params.append(key, cents.toString());
          }
          continue;
        }

        if (value !== '') {
          params.append(key, value);
        }
      }
    });

    return params;
  }

  /* ------------------------------------------------------------------
     Fetch & Render via Section Rendering API
     ------------------------------------------------------------------ */

  async fetchAndRender(searchParams, updateHistory = true) {
    // Abort any in-flight request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.showLoading();

    // Build the fetch URL
    const url = `${this.collectionUrl}?${searchParams.toString()}&sections=${this.sectionId}`;

    // Update browser URL (without section param).
    // pushState (not replace) so the browser Back button restores the previous
    // sort/filter state; popstate re-renders it. See bindEvents().
    const historyUrl = `${this.collectionUrl}?${searchParams.toString()}`;
    if (updateHistory) {
      window.history.pushState({ facets: true }, '', historyUrl);
    }

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const sectionHtml = data[this.sectionId];

      if (sectionHtml) {
        this.renderSectionHtml(sectionHtml);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was intentionally aborted, ignore
        return;
      }

      console.error('[SkinTalk] Facets fetch error:', error);

      // Fallback: do a full page navigation
      window.location.href = `${this.collectionUrl}?${searchParams.toString()}`;
    } finally {
      this.hideLoading();
      this.abortController = null;
    }
  }

  /* ------------------------------------------------------------------
     Render Section HTML
     ------------------------------------------------------------------ */

  renderSectionHtml(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Update product grid
    this.swapContent(
      `#CollectionProducts-${this.sectionId}`,
      doc
    );

    // Update toolbar (product count)
    this.swapContent(
      `#CollectionToolbar-${this.sectionId}`,
      doc
    );

    // Update active filters
    this.swapContent(
      `#ActiveFilters-${this.sectionId}`,
      doc
    );

    // Update pagination
    this.swapContent(
      `#CollectionPagination-${this.sectionId}`,
      doc
    );

    // Update sidebar filters (keep in sync)
    this.swapContent(
      `#FacetsSidebar-${this.sectionId}`,
      doc
    );

    // Update drawer filters (keep in sync)
    this.swapContent(
      `#FacetsDrawer-${this.sectionId}`,
      doc
    );

    // Update horizontal filters
    this.swapContent(
      `#FacetsHorizontal-${this.sectionId}`,
      doc
    );

    // Re-initialize scroll animations for new product cards
    if (SkinTalk.initScrollAnimations) {
      SkinTalk.initScrollAnimations();
    }

    // Restore view preference after re-render
    this.restoreViewPreference();

    // Re-bind price inputs on new facets
    this.qsa('[data-price-min], [data-price-max]').forEach((input) => {
      // Check if listener already attached via data attribute
      if (!input.dataset.bound) {
        input.addEventListener('input', () =>
          this.debouncedPriceChange(input.closest('[data-facets-form]'))
        );
        input.dataset.bound = 'true';
      }
    });

    // Publish event to SkinTalk event bus
    SkinTalk.publish('facets:updated', {
      sectionId: this.sectionId,
      url: window.location.href,
    });

    // Scroll to top of grid on filter change
    const grid = document.getElementById(`CollectionProductGrid`);
    if (grid) {
      const gridTop = grid.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: gridTop, behavior: 'smooth' });
    }
  }

  /* ------------------------------------------------------------------
     Swap Content Helper
     ------------------------------------------------------------------ */

  swapContent(selector, sourceDoc) {
    const target = this.querySelector(selector) || document.querySelector(selector);
    const source = sourceDoc.querySelector(selector);

    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  /* ------------------------------------------------------------------
     Loading State
     ------------------------------------------------------------------ */

  showLoading() {
    const loader = document.getElementById(`CollectionLoading-${this.sectionId}`);
    if (loader) {
      loader.removeAttribute('hidden');
    }

    // Reduce opacity of product grid during loading
    const grid = this.qs(`#CollectionProducts-${this.sectionId}`);
    if (grid) {
      grid.style.opacity = '0.5';
      grid.style.pointerEvents = 'none';
      grid.style.transition = 'opacity 0.2s ease';
    }
  }

  hideLoading() {
    const loader = document.getElementById(`CollectionLoading-${this.sectionId}`);
    if (loader) {
      loader.setAttribute('hidden', '');
    }

    const grid = this.qs(`#CollectionProducts-${this.sectionId}`);
    if (grid) {
      grid.style.opacity = '';
      grid.style.pointerEvents = '';
      grid.style.transition = '';
    }
  }

  /* ------------------------------------------------------------------
     Filter Drawer (Mobile)
     ------------------------------------------------------------------ */

  openFilterDrawer() {
    const drawer = document.getElementById(`FilterDrawer-${this.sectionId}`);
    if (!drawer) return;

    drawer.removeAttribute('hidden');

    // Force reflow before adding class for transition
    void drawer.offsetHeight;

    drawer.classList.add('is-open');

    SkinTalk.lockBodyScroll();

    // Focus management
    const panel = drawer.querySelector('.filter-drawer__panel');
    if (panel) {
      SkinTalk.trapFocus(panel, drawer.querySelector('.filter-drawer__close'));
    }

    // Update ARIA on trigger button
    this.qsa('[data-open-filters]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'true');
    });
  }

  closeFilterDrawer() {
    const drawer = document.getElementById(`FilterDrawer-${this.sectionId}`);
    if (!drawer || !drawer.classList.contains('is-open')) return;

    drawer.classList.remove('is-open');

    SkinTalk.unlockBodyScroll();
    SkinTalk.removeTrapFocus();

    // Hide after transition
    const handleTransitionEnd = () => {
      if (!drawer.classList.contains('is-open')) {
        drawer.setAttribute('hidden', '');
      }
      drawer.removeEventListener('transitionend', handleTransitionEnd);
    };
    drawer.addEventListener('transitionend', handleTransitionEnd);

    // Fallback in case transitionend doesn't fire
    setTimeout(() => {
      if (!drawer.classList.contains('is-open')) {
        drawer.setAttribute('hidden', '');
      }
    }, 400);

    // Update ARIA on trigger button
    this.qsa('[data-open-filters]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    // Return focus to trigger
    const trigger = this.qs('[data-open-filters]');
    if (trigger) {
      trigger.focus();
    }
  }

  /* ------------------------------------------------------------------
     Grid View Toggle
     ------------------------------------------------------------------ */

  setGridColumns(columns) {
    const grid = this.qs(`#CollectionProducts-${this.sectionId}`) ||
                 document.getElementById(`CollectionProducts-${this.sectionId}`);

    if (grid) {
      grid.style.setProperty('--grid-columns-desktop', columns);
      grid.dataset.columnsDesktop = columns;
    }

    // Update button active states
    this.qsa('[data-view-columns]').forEach((btn) => {
      const isActive = btn.dataset.viewColumns === columns;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    // Persist to localStorage
    try {
      localStorage.setItem('skintalk-grid-columns', columns);
    } catch (e) {
      // localStorage not available, silently fail
    }
  }

  restoreViewPreference() {
    try {
      const saved = localStorage.getItem('skintalk-grid-columns');
      if (saved) {
        this.setGridColumns(saved);
      }
    } catch (e) {
      // localStorage not available
    }
  }
}

  // Register the custom element
  customElements.define('facet-filters-form', FacetFiltersForm);
} // end if (!customElements.get('facet-filters-form'))
} // end defineFacetFiltersForm

// SkinTalkElement is defined in global.js which loads AFTER this script.
// Wait for it before defining the custom element.
if (typeof SkinTalkElement !== 'undefined') {
  defineFacetFiltersForm();
} else {
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof SkinTalkElement !== 'undefined') {
      defineFacetFiltersForm();
    } else {
      console.error('[SkinTalk] SkinTalkElement not available after DOMContentLoaded — facets disabled');
    }
  });
}


/* ==========================================================================
   2. RE-INIT ON SHOPIFY EDITOR EVENTS
   ========================================================================== */

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    const facetForm = section.querySelector('facet-filters-form');
    if (facetForm && facetForm.connectedCallback) {
      facetForm.connectedCallback();
    }
  });
}
